/**
 * useVideoListings — React Query hook for
 * `ContentReaderPort.listVideoListings` (029 US2 / FR-007).
 *
 * Wraps the active `ContentReaderPort` (resolved via {@link useContent}) in a
 * `useQuery` call. Drives the `/lakiruutu` surface (`LakiruutuRoute.tsx`, T024),
 * which renders the published `video_listing` entries. Mirrors the established
 * member-read hook pattern (`useCuratedCards`): the
 * adapter stays a plain fetcher (D-046), this hook owns cache + loading
 * semantics, and the route maps React Query's state matrix onto skeleton /
 * boundary-throw / empty / populated.
 *
 * queryKey: `['content', 'videoListings', limit, locale]` — flat, with `limit`
 * and `locale` included so distinct requests do not alias in the cache.
 *
 * Authoritative spec references:
 *   - `specs/029-cms-driven-member-surfaces/contracts/trpc-content-procedures.md`.
 *   - `docs/governance/decisions/D-046.md` D-046 (React Query integration).
 *   - `apps/web/src/content/ContentReaderPort.ts` (the port being wrapped).
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { VideoListing } from '../ContentReaderPort.js';
import { CONTENT_STALE_TIME } from './queryConfig.js';
import { useContent } from './useContent.js';

/**
 * Options for {@link useVideoListings}. `limit` is a soft cap on the small
 * `/lakiruutu` set; `locale` is forwarded verbatim to the port (fi default at
 * the adapter, D-051). See `useCuratedCards` for the `?: T | undefined`
 * rationale under `exactOptionalPropertyTypes`.
 */
export interface UseVideoListingsOptions {
  readonly limit?: number | undefined;
  readonly locale?: string | undefined;
}

/**
 * Fetch the `/lakiruutu` video listings ordered by `order` ascending. Returns
 * the standard React Query result; `LakiruutuRoute` renders a skeleton while
 * pending, throws on `isError` (caught by the route's `PortErrorBoundary`), the
 * empty state when `data` is `[]`, and the list once `data` resolves.
 */
export function useVideoListings(
  options: UseVideoListingsOptions = {},
): UseQueryResult<readonly VideoListing[], Error> {
  const { limit, locale } = options;
  const port = useContent();

  return useQuery<readonly VideoListing[], Error>({
    queryKey: ['content', 'videoListings', limit, locale],
    queryFn: () => port.listVideoListings({ limit, locale }),
    staleTime: CONTENT_STALE_TIME,
  });
}
